/**
 * Proximity interaction — US 01.02, pure.
 *
 * Each NPC owns a 5-tile "C"-shaped zone: the 3 tiles adjacent to its facing
 * direction (front + front diagonals) plus the 2 flanking side tiles. The
 * tile directly behind and both rear diagonals are excluded — an NPC can
 * never be engaged from behind, and the shape makes that rule learnable.
 */
import { TILE } from '@shared/constants.js';
import type { Facing } from './grid.js';

export interface NpcPlacement {
  id: string;
  /** Tile the NPC stands on. NPCs are static with fixed facing (decision). */
  col: number;
  row: number;
  facing: Facing;
}

interface TileRef {
  col: number;
  row: number;
}

/** The C-shape, in tile coordinates. */
export function interactionZone(npc: NpcPlacement): TileRef[] {
  const { col, row, facing } = npc;
  switch (facing) {
    case 'down':
      return [
        { col: col - 1, row: row + 1 }, // front-left
        { col, row: row + 1 }, //          front
        { col: col + 1, row: row + 1 }, // front-right
        { col: col - 1, row }, //          flank left
        { col: col + 1, row }, //          flank right
      ];
    case 'up':
      return [
        { col: col - 1, row: row - 1 },
        { col, row: row - 1 },
        { col: col + 1, row: row - 1 },
        { col: col - 1, row },
        { col: col + 1, row },
      ];
    case 'left':
      return [
        { col: col - 1, row: row - 1 },
        { col: col - 1, row },
        { col: col - 1, row: row + 1 },
        { col, row: row - 1 },
        { col, row: row + 1 },
      ];
    case 'right':
      return [
        { col: col + 1, row: row - 1 },
        { col: col + 1, row },
        { col: col + 1, row: row + 1 },
        { col, row: row - 1 },
        { col, row: row + 1 },
      ];
  }
}

function inZone(npc: NpcPlacement, centreX: number, centreY: number): boolean {
  const col = Math.floor(centreX / TILE);
  const row = Math.floor(centreY / TILE);
  return interactionZone(npc).some((t) => t.col === col && t.row === row);
}

function centreDistanceSq(npc: NpcPlacement, x: number, y: number): number {
  const nx = npc.col * TILE + TILE / 2;
  const ny = npc.row * TILE + TILE / 2;
  return (nx - x) ** 2 + (ny - y) ** 2;
}

/**
 * The single NPC whose zone contains the player's collision-box centre —
 * or null. Overlapping zones: nearest centre-to-centre wins; ties break to
 * the lowest NPC id. Only one "!" indicator ever shows (US 01.02).
 */
export function activeNpc(
  npcs: readonly NpcPlacement[],
  centreX: number,
  centreY: number,
): NpcPlacement | null {
  let best: NpcPlacement | null = null;
  let bestDist = Infinity;
  for (const npc of npcs) {
    if (!inZone(npc, centreX, centreY)) continue;
    const d = centreDistanceSq(npc, centreX, centreY);
    if (d < bestDist || (d === bestDist && best !== null && npc.id < best.id)) {
      best = npc;
      bestDist = d;
    }
  }
  return best;
}
